//@flow
import { select, call, put, fork, all, take } from "redux-saga/effects";
import { eventChannel } from "redux-saga";
import { navigate } from "../navigation/navigatorHelper";
import { LOGOUT, LOGIN_ERROR } from "../login/constants";

import { modifyUser } from "../user/actions";
import { changeFeeling } from "../feeling/actions";
import { getLatestConversationsRequest } from "../message/actions";

import socket from "../socket.js";
import {
  MESSAGE_GOT_NEW,
  MESSAGE_LIKE_REQUESTING,
  MESSAGE_LIKE_SUCCESS,
  MESSAGE_OPEN_CHAT,
  REMOVE_MESSAGE_SUCCESS,
} from "../message/constants";
import { getUserId } from "../user/selectors";
import { getConversationId, getReceivers } from "../message/selectors";
import {
  NOTIFICATION_NEW_MESSAGES,
  NOTIFICATION_MESSAGE_INCREMENT,
  NOTIFICATION_NEW_HOME,
  NOTIFICATION_PULSE_SET,
  NOTIFICATION_PULSE_ADD,
  NOTIFICATION_HOME_INCREMENT,
  NOTIFICATION_NEW_RANDOM_COFFEE,
  NOTIFICATION_GENERAL_ADD,
  NOTIFICATION_GENERAL_SET,
  NOTIFICATION_TEAM_ADD,
  NOTIFICATION_NEW_ACHIEVEMENT,
  NOTIFICATION_RESOURCE_INCREMENT,
} from "../notification/constants";
import { GLOSSARY_RECEIVE_WORDS } from "../glossary/constants";
import {
  IDEA_CHANGE,
  IDEA_USER_DELETE_SUCCESS,
  IDEA_NEW,
} from "../idea/constants";
import { POLL_RECEIVED } from "../poll/constants";
import {
  USER_CAMPAIGN_SET_USER_RANK,
  USER_CAMPAIGN_SET_TEAM_RANK,
  USER_CAMPAIGN_SET_TEAM,
  USER_CAMPAIGN_DELETE,
  USER_CAMPAIGN_GET_SUCCESS,
  UPDATE_REVIEW_SUCCESS,
  UPDATE_STEP_FEEDBACK_FILE,
  ADD_STEP_COMMENT_SUCCESS,
  SEND_RESPONSE_SUCCESS,
} from "../userCampaign/constants";

import {
  MOBILE_CONNECTION_START,
  MOBILE_CONNECTION_STEP_CHANGE,
} from "./constants";
import {
  getCurrentUserCampaignRequest,
  getEventsRequest,
} from "../userCampaign/actions";
import { GET_COACHING_SESSION_SUCCESS } from "@microdoing/shared/services/coaching/constants";
import { getCoachingSessionId } from "@microdoing/shared/services/coaching/selectors";
import { getCampaignId } from "@microdoing/shared/services/userCampaign/selectors";
import { GET_CAMPAIGN_RESOURCES_REQUESTING } from "@microdoing/shared/services/resources/constants";
import { getRoute } from "../nav/selectors";

const endPoints = {
  validatedTaskNotification: "/v1/notification/send",
};

const createSocketChannel = channel => {
  return socket =>
    eventChannel(emit => {
      const handler = data => {
        emit(data);
      };
      socket.on(channel, handler);
      return () => {
        socket.off(channel, handler);
      };
    });
};

export const sendMessage = payload => {
  socket.emit("message", payload);
};

export const emitGlossaryWord = payload => {
  socket.emit("glossary-send-word", payload);
};

// wrapping function for socket.on
const connect = () => {
  return new Promise(resolve => {
    socket.on("connect", () => {
      resolve(socket);
    });
  });
};

const createStepFeedbackChannel = createSocketChannel("stepFeedback");

const createGlossarySocketChannel = createSocketChannel("glossary-new-word");

const createIdeaSocketChannel = createSocketChannel("idea");

const createDeleteIdeaSocketChannel = createSocketChannel("idea-delete");

const createNewIdeaSocketChannel = createSocketChannel("idea-new");

const createChatSocketChannel = createSocketChannel("priorMessages");

const createMessageSocketChannel = createSocketChannel("incomingMessage");

const createLikeMessageSocketChannel = createSocketChannel("likeMessage");

const createRemoveMessageSocketChannel = createSocketChannel("removeMessage");

const createStatsSocketChannel = createSocketChannel("onboardingStatsEvent");

const createUserSocketChannel = createSocketChannel("userEvent");

const createPollSocketChannel = createSocketChannel("poll");

const createAchievementChannel = createSocketChannel("achievement");

const createNotificationSocketChannel = createSocketChannel(
  "notificationEvent",
);

const createHomeNotificationSocketChannel = createSocketChannel(
  "incrementHomeNotifications",
);

const createGeneralNotificationSocketChannel = createSocketChannel(
  "newNotification",
);

const createPulseSocketChannel = createSocketChannel("sendPulse");

const createUserCampaignSocketChannel = createSocketChannel("userCampaign");

const createNewUserCampaignSocketChannel = createSocketChannel(
  "newUserCampaign",
);

const createDeleteCampaignSocketChannel = createSocketChannel(
  "campaign-delete",
);

const createCampaignRankSocketChannel = createSocketChannel("campaignRank");

const createTeamSocketChannel = createSocketChannel("teamEvent");

const createMobileConnectionSocketChannel = createSocketChannel(
  "mobile-connection-step-change",
);

const createStartMobileConnectionSocketChannel = createSocketChannel(
  "mobile-connection-start",
);

const createCoachingSocketChannel = createSocketChannel(
  "coaching-session-update",
);

const createCampaignResourcesSocketChannel = createSocketChannel(
  "campaign-resource-update",
);

const createNavigateSocketChannel = createSocketChannel(
  "mobile-connection-navigate",
);

const createStepFeedbackFileUpdateChannel = createSocketChannel(
  "stepFeedback-file-update",
);

const createStepCommentAnswerChannel = createSocketChannel(
  "step-comment-answer",
);

const createUpdateEventsChannel = createSocketChannel("updateEvents");

const listenNavigateEvent = function* (newSocket) {
  // then create a socket channel
  const socketChannel = yield call(createNavigateSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    navigate(payload);
    // yield put({ type: NAVIGATE, payload });
  }
  // if user is forbidden log him out
};

const listenStepFeedbackFileUpdateEvent = function* (newSocket) {
  const socketChannel = yield call(
    createStepFeedbackFileUpdateChannel,
    newSocket,
  );

  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: UPDATE_STEP_FEEDBACK_FILE, payload });
  }
  // if user is forbidden log him out
};

const listenStepCommentAnswerEvent = function* (newSocket) {
  const socketChannel = yield call(createStepCommentAnswerChannel, newSocket);

  while (true) {
    const payload = yield take(socketChannel);
    yield put({
      type: SEND_RESPONSE_SUCCESS,
      payload: { response: payload, stepCommentId: payload.stepCommentId },
    });
  }
  // if user is forbidden log him out
};

const listenStepFeedbackEvent = function* (newSocket) {
  const socketChannel = yield call(createStepFeedbackChannel, newSocket);

  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: UPDATE_REVIEW_SUCCESS, payload });
  }
  // if user is forbidden log him out
};

const listenCampaignResourcesEvent = function* (newSocket) {
  // then create a socket channel
  const socketChannel = yield call(
    createCampaignResourcesSocketChannel,
    newSocket,
  );

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    const campaignId = yield select(getCampaignId);
    if (campaignId === payload.campaignId) {
      const userId = yield select(getUserId);
      yield put({
        type: NOTIFICATION_RESOURCE_INCREMENT,
      });
      yield put({
        type: GET_CAMPAIGN_RESOURCES_REQUESTING,
        payload: { userId, campaignId },
      });
    }
  }
  // if user is forbidden log him out
};

const listenCoachingEvent = function* (newSocket) {
  // then create a socket channel
  const socketChannel = yield call(createCoachingSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    const coachingSessionId = yield select(getCoachingSessionId);
    if (!coachingSessionId || coachingSessionId === payload._id) {
      yield put({ type: GET_COACHING_SESSION_SUCCESS, payload });
    }
  }
  // if user is forbidden log him out
};

const listenChatEvent = function* (newSocket) {
  // then create a socket channel
  const socketChannel = yield call(createChatSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: MESSAGE_OPEN_CHAT, payload });
  }
  // if user is forbidden log him out
};

const listenMessageEvent = function* (newSocket) {
  // then create a socket channel
  const socketChannel = yield call(createMessageSocketChannel, newSocket);
  try {
    while (true) {
      const payload = yield take(socketChannel);
      const userId = yield select(getUserId);
      const receivers = yield select(getReceivers);
      const conversationId = yield select(getConversationId);
      const currentRoute = yield select(getRoute);

      // basé l'update sur la conversation sélectionné plutôt que le receiver - en fonction
      // means chat is currently open

      if (
        payload.user._id === userId ||
        receivers.filter(el => el._id === payload.user._id).length > 0 ||
        payload.conversationId === conversationId
      ) {
        yield put({ type: MESSAGE_GOT_NEW, payload });
      }
      // we are not the sender
      if (payload.user._id !== userId && currentRoute !== "Chat") {
        yield put({ type: NOTIFICATION_MESSAGE_INCREMENT });
      }
      yield put(getLatestConversationsRequest({ userId }));
    }
  } catch (error) {
    console.log(error);
  }
};

const listenLikeMessageEvent = function* (newSocket) {
  const socketChannel = yield call(createLikeMessageSocketChannel, newSocket);
  while (true) {
    const payload = yield take(socketChannel);
    try {
      yield put({ type: MESSAGE_LIKE_SUCCESS, payload });
    } catch (e) {
      console.log(e);
    }
  }
};

const listenRemoveMessageEvent = function* (newSocket) {
  const socketChannel = yield call(createRemoveMessageSocketChannel, newSocket);
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: REMOVE_MESSAGE_SUCCESS, payload });
  }
};

const listenUserEvent = function* (newSocket) {
  // then create a socket channel
  const socketChannel = yield call(createUserSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    // if user is forbidden log him out
    if (payload.isForbidden) {
      yield put({ type: LOGOUT });
      yield put({ type: LOGIN_ERROR, error: { message: "isForbidden" } });
    } else {
      if (payload.pulseNotifications) {
        yield put({
          type: NOTIFICATION_PULSE_SET,
          payload: payload.pulseNotifications,
        });
      }
      if (payload.teamNotifications) {
        yield put({
          type: NOTIFICATION_TEAM_ADD,
          payload: payload.teamNotifications,
        });
      }
      yield put(modifyUser(payload));
    }
  }
};

const listenAchievementEvent = function* (newSocket) {
  const socketChannel = yield call(createAchievementChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put({
      type: NOTIFICATION_NEW_ACHIEVEMENT,
      payload,
    });
  }
};

const listenNotificationEvent = function* (newSocket) {
  const socketChannel = yield call(createNotificationSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    const {
      newMessageNotifications,
      newHomeNotifications,
      newRandomCoffeeNotifications,
      newGeneralNotifications,
    } = payload;
    if (newMessageNotifications) {
      yield put({
        type: NOTIFICATION_NEW_MESSAGES,
        payload: newMessageNotifications,
      });
      const userId = yield select(getUserId);
      yield put(getLatestConversationsRequest({ userId }));
    }
    if (newHomeNotifications) {
      yield put({ type: NOTIFICATION_NEW_HOME, payload: newHomeNotifications });
    }
    if (newRandomCoffeeNotifications) {
      yield put({
        type: NOTIFICATION_NEW_RANDOM_COFFEE,
        payload: newRandomCoffeeNotifications,
      });
    }
    if (newGeneralNotifications) {
      yield put({
        type: NOTIFICATION_GENERAL_SET,
        payload: newGeneralNotifications,
      });
    }
  }
};

const listenHomeNotifications = function* (newSocket) {
  const socketChannel = yield call(
    createHomeNotificationSocketChannel,
    newSocket,
  );

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: NOTIFICATION_HOME_INCREMENT, payload });
  }
};

const listenGeneralNotifications = function* (newSocket) {
  const socketChannel = yield call(
    createGeneralNotificationSocketChannel,
    newSocket,
  );

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: NOTIFICATION_GENERAL_ADD, payload });
  }
};

const listenPulseEvent = function* (newSocket) {
  const socketChannel = yield call(createPulseSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: NOTIFICATION_PULSE_ADD, payload });
  }
};

const listenStatsEvent = function* (newSocket) {
  const socketChannel = yield call(createStatsSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put(changeFeeling(payload));
    // yield put(newNotification(payload));
  }
};

const listenGlossaryEvent = function* (newSocket) {
  const socketChannel = yield call(createGlossarySocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: GLOSSARY_RECEIVE_WORDS, payload });
  }
};

const listenDeleteIdeaEvent = function* (newSocket) {
  const socketChannel = yield call(createDeleteIdeaSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: IDEA_USER_DELETE_SUCCESS, payload });
  }
};

const listenNewIdeaEvent = function* (newSocket) {
  const socketChannel = yield call(createNewIdeaSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: IDEA_NEW, payload });
  }
};

const listenIdeaEvent = function* (newSocket) {
  const socketChannel = yield call(createIdeaSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: IDEA_CHANGE, payload });
  }
};

const listenPollEvent = function* (newSocket) {
  const socketChannel = yield call(createPollSocketChannel, newSocket);

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: POLL_RECEIVED, payload });
  }
};

const listenUserCampaignEvent = function* (newSocket) {
  const socketChannel = yield call(createUserCampaignSocketChannel, newSocket);
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: USER_CAMPAIGN_GET_SUCCESS, payload });
  }
};

const listenNewUserCampaignEvent = function* (newSocket) {
  const socketChannel = yield call(
    createNewUserCampaignSocketChannel,
    newSocket,
  );
  while (true) {
    const payload = yield take(socketChannel);
    const userId = yield select(getUserId);
    yield put(getCurrentUserCampaignRequest({ userId }));
  }
};

const listenCampaignRankEvent = function* (newSocket) {
  const socketChannel = yield call(createCampaignRankSocketChannel, newSocket);
  while (true) {
    const payload = yield take(socketChannel);
    const { userRank, teamRank, campaignId } = payload;
    const currentCampaignId = yield select(
      state => state.userCampaign.campaignSelected.campaignId,
    );

    if (currentCampaignId === campaignId) {
      yield put({ type: USER_CAMPAIGN_SET_USER_RANK, payload: userRank });
      yield put({ type: USER_CAMPAIGN_SET_TEAM_RANK, payload: teamRank });
    }
  }
};

const listenDeleteCampaignEvent = function* (newSocket) {
  const socketChannel = yield call(
    createDeleteCampaignSocketChannel,
    newSocket,
  );

  // then put the new data into the reducer
  while (true) {
    const payload = yield take(socketChannel);
    const { campaignId } = payload;
    const state = yield select();
    const currentCampaignId = state.userCampaign.campaignSelected.campaignId;
    const currentRoute = state.nav.route;

    if (currentCampaignId === campaignId && currentRoute === "Home") {
      navigate("Main");
    }
    yield put({ type: USER_CAMPAIGN_DELETE, payload });
    const userId = getUserId(state);
    yield put(getCurrentUserCampaignRequest({ userId }));
  }
};

const listenTeamEvent = function* (newSocket) {
  const socketChannel = yield call(createTeamSocketChannel, newSocket);
  while (true) {
    const payload = yield take(socketChannel);
    const team = payload;
    const currentCampaignId = yield select(
      state => state.userCampaign.campaignSelected.campaignId,
    );
    // const currentTeamId = yield select(state => state.userCampaign.team.campaignId === currentCampaignId);
    if (team.campaignId === currentCampaignId) {
      yield put({ type: USER_CAMPAIGN_SET_TEAM, payload: team });
    }
  }
};

const listenStartMobileConnectionEvent = function* (newSocket) {
  const socketChannel = yield call(
    createStartMobileConnectionSocketChannel,
    newSocket,
  );

  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: MOBILE_CONNECTION_START, payload });
    // navigate('mobile-connection')
    navigate("Action");
  }
};

const listenMobileConnectionStepChangeEvent = function* (newSocket) {
  const socketChannel = yield call(
    createMobileConnectionSocketChannel,
    newSocket,
  );
  while (true) {
    const payload = yield take(socketChannel);
    yield put({ type: MOBILE_CONNECTION_STEP_CHANGE, payload });
  }
};

const listenUpdateEventsEvent = function* (newSocket) {
  const socketChannel = yield call(createUpdateEventsChannel, newSocket);
  while (true) {
    yield take(socketChannel);
    const campaignId = yield select(getCampaignId);
    yield put(getEventsRequest({ campaignId }));
  }
};

const channelFlow = function* () {
  // connect to the server
  const newSocket = yield call(connect);
  yield fork(listenStatsEvent, newSocket);
  yield fork(listenUserEvent, newSocket);
  yield fork(listenNotificationEvent, newSocket);
  yield fork(listenChatEvent, newSocket);
  yield fork(listenMessageEvent, newSocket);
  yield fork(listenLikeMessageEvent, newSocket);
  yield fork(listenPulseEvent, newSocket);
  yield fork(listenHomeNotifications, newSocket);
  yield fork(listenGlossaryEvent, newSocket);
  yield fork(listenGeneralNotifications, newSocket);
  yield fork(listenIdeaEvent, newSocket);
  yield fork(listenNewIdeaEvent, newSocket);
  yield fork(listenDeleteIdeaEvent, newSocket);
  yield fork(listenPollEvent, newSocket);
  yield fork(listenAchievementEvent, newSocket);
  yield fork(listenUserCampaignEvent, newSocket);
  yield fork(listenNewUserCampaignEvent, newSocket);
  yield fork(listenCampaignRankEvent, newSocket);
  yield fork(listenTeamEvent, newSocket);
  yield fork(listenMobileConnectionStepChangeEvent, newSocket);
  yield fork(listenStartMobileConnectionEvent, newSocket);
  yield fork(listenDeleteCampaignEvent, newSocket);
  yield fork(listenCoachingEvent, newSocket);
  yield fork(listenCampaignResourcesEvent, newSocket);
  yield fork(listenNavigateEvent, newSocket);
  yield fork(listenStepFeedbackEvent, newSocket);
  yield fork(listenStepFeedbackFileUpdateEvent, newSocket);
  yield fork(listenStepCommentAnswerEvent, newSocket);
  yield fork(listenUpdateEventsEvent, newSocket);
  yield fork(listenRemoveMessageEvent, newSocket);
};

function* trackWatcher() {
  yield all([channelFlow()]);
}

export default trackWatcher;
